import { useEffect } from 'react';
import { useLogger } from './useLogger';
import { useToast } from '@/hooks/use-toast';
import { handlePermissionError, PermissionError } from '@/lib/permissionService';
import * as Sentry from '@sentry/react';

export const useGlobalErrorHandler = () => {
  const { logError, logNetworkError } = useLogger();
  const { toast } = useToast();

  const handleErrorWithToast = (error: any, context?: string) => {
    // Handle permission errors first
    if (error && (error.code === 'PGRST116' || error.message?.includes('RLS') || error.status === 401 || error.status === 403)) {
      const permissionError = handlePermissionError(error);
      
      // Show toast based on error type
      switch (permissionError.code) {
        case 'FORBIDDEN':
          toast({
            title: "Access Denied",
            description: permissionError.message,
            variant: "destructive"
          });
          break;
        case 'UNAUTHORIZED':
          toast({
            title: "Session Expired",
            description: permissionError.message,
            variant: "destructive"
          });
          break;
        case 'NETWORK_ERROR':
          toast({
            title: "Network Error",
            description: permissionError.message,
            variant: "destructive"
          });
          break;
      }
      return;
    }

    // Handle other errors with existing logic
    if (error?.response) {
      const response = error.response;
      logNetworkError(
        response.config?.url || 'unknown',
        response.status || 0,
        error.message || 'Network error'
      );
      
      toast({
        title: "Network Error",
        description: `Request failed with status ${response.status}`,
        variant: "destructive"
      });
    } else {
      logError(error || 'Unknown error', {
        component: context || 'global',
        action: 'error'
      });
      
      toast({
        title: "Error",
        description: error?.message || 'An unexpected error occurred',
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      handleErrorWithToast(event.reason, 'unhandled_rejection');
      
      // Prevent default browser behavior
      event.preventDefault();
    };

    // Handle uncaught JavaScript errors
    const handleError = (event: ErrorEvent) => {
      console.error('Uncaught error:', event.error);
      
      handleErrorWithToast(event.error || event.message, 'uncaught_error');
    };

    // Handle React errors (if not caught by error boundaries)
    const handleReactError = (error: Error, errorInfo: any) => {
      console.error('React error:', error, errorInfo);
      
      logError(error, {
        component: 'react',
        action: 'component_error',
        componentStack: errorInfo.componentStack
      });
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [logError, logNetworkError, toast, handleErrorWithToast]);
};